import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './modal.css';

/**
 * ModalBase - Base component for all modals in the application
 * 
 * Features:
 * - Portal rendering to document.body (avoids stacking context issues)
 * - Z-index management (backdrop: 1040, modal: 1050)
 * - ESC key support
 * - Backdrop click to close
 * - Focus management (auto-focus, restore on close)
 * - Scroll lock on body
 * - Theme support (light/dark)
 * - Accessibility (role, aria-modal, aria-label)
 * 
 * Fixed interaction bug:
 * - Removed onClick from .modal-root (was causing all clicks to close)
 * - Added stopPropagation on click, mousedown, pointerdown in .modal-viewport
 * - Now modal only closes on backdrop click, close button, or ESC
 */
export default function ModalBase({
  isOpen,
  onClose,
  labelledBy,
  children,
  className,
  viewportClassName,
  backdropClassName,
  initialFocusRef,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  ariaLabel = 'Modal'
}) {
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    
    // Handle ESC key
    const handleKeyDown = event => {
      if (closeOnEsc && event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, onClose, closeOnEsc]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    
    // Add modal-open class to body and lock scroll
    document.body.classList.add('modal-open');
    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    
    return () => {
      document.body.classList.remove('modal-open');
      style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (previousFocusRef.current instanceof HTMLElement) {
        // Restore focus to the element that opened the modal for screen reader continuity.
        setTimeout(() => {
          previousFocusRef.current?.focus({ preventScroll: true });
        }, 0);
      }
      return undefined;
    }
    
    const focusTarget = initialFocusRef?.current ?? dialogRef.current;
    // Ensure the first interactive element receives focus when the modal opens.
    focusTarget?.focus({ preventScroll: true });
    return undefined;
  }, [initialFocusRef, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = event => {
    if (closeOnBackdropClick && event.target === backdropRef.current) {
      onClose();
    }
  };

  const handleModalClick = event => {
    // Stop propagation to prevent backdrop click
    event.stopPropagation();
  };

  const handleModalMouseDown = event => {
    // Stop propagation for libraries that close on mousedown/pointerdown
    event.stopPropagation();
  };

  const handleModalPointerDown = event => {
    // Stop propagation for libraries that close on pointerdown
    event.stopPropagation();
  };

  return createPortal(
    <>
      <div 
        ref={backdropRef}
        className={backdropClassName || 'modal-backdrop'} 
        aria-hidden="true" 
        onClick={handleBackdropClick}
        role="presentation"
      />
      <div 
        className="modal-root" 
        role="presentation"
      >
        <div 
          className={viewportClassName || "modal-viewport"}
          onClick={handleModalClick}
          onMouseDown={handleModalMouseDown}
          onPointerDown={handleModalPointerDown}
        >
          <div
            ref={dialogRef}
            className={className || 'modal modal-content w-full max-w-3xl p-5 md:p-6 focus-visible:outline-none'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-label={ariaLabel}
            tabIndex={-1}
          >
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
